import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import Layout from './Layout';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import axios from 'axios';
import { Building, TrendingUp, BarChart3, Plus, Filter, Calendar, Euro, ArrowUp, ArrowDown, Trash2, Pencil } from 'lucide-react';
import { BACKEND_URL } from '../utils/config';

const API = `${BACKEND_URL}/api`;

const ManagementControl = () => {
  const [controlData, setControlData] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [budgetLines, setBudgetLines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cashFlowData, setCashFlowData] = useState(null);
  
  // Filters - focus on HolidayWorld for now
  const [selectedCompany, setSelectedCompany] = useState('');
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  
  // Budget mode (for next year forecasts)
  const [viewMode, setViewMode] = useState('actual'); // 'actual' or 'budget'
  const [budgetForecasts, setBudgetForecasts] = useState([]);
  const [editingForecast, setEditingForecast] = useState({});
  
  // Modals
  const [showNewCompany, setShowNewCompany] = useState(false);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [showNewBudgetLine, setShowNewBudgetLine] = useState(false);
  const [showEditBudgetLine, setShowEditBudgetLine] = useState(false);
  const [showCompanyLogos, setShowCompanyLogos] = useState(false);
  const [logoUploading, setLogoUploading] = useState(null);
  const [logoRefresh, setLogoRefresh] = useState(0);

  const handleLogoUpload = async (companyId, file) => {
    if (!file) return;
    setLogoUploading(companyId);
    try {
      const token = localStorage.getItem('hw_token');
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/companies/${companyId}/logo`, fd, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Logo caricato con successo!');
      setLogoRefresh(v => v + 1);
      fetchCompanies();
    } catch (e) {
      alert('Errore caricamento logo: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setLogoUploading(null);
    }
  };
  
  // Forms
  const [newCompany, setNewCompany] = useState({ name: '', code: '', description: '' });
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  const [newBudgetLine, setNewBudgetLine] = useState({ 
    name: '', 
    type: 'revenue', 
    forecast: '',
    order: ''
  });
  const [editBudgetLine, setEditBudgetLine] = useState({ 
    id: '',
    name: '', 
    type: 'revenue', 
    forecast: '',
    order: ''
  });
  
  // AI Insights states
  const [showAIInsights, setShowAIInsights] = useState(false);
  const [aiInsights, setAiInsights] = useState(null);
  const [loadingAI, setLoadingAI] = useState(false);

  useEffect(() => {
    fetchCompanies();
  }, []);

  useEffect(() => {
    if (selectedCompany && selectedYear) {
      if (viewMode === 'actual') {
        fetchControlData();
        fetchCashFlow();
      } else {
        fetchBudgetForecasts();
      }
    }
  }, [selectedCompany, selectedYear, viewMode]);

  const fetchCompanies = async () => {
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(`${API}/companies`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCompanies(response.data || []);
      
      // Auto-select first company if available
      if (response.data && response.data.length > 0 && !selectedCompany) {
        setSelectedCompany(response.data[0].id);
      }
    } catch (error) {
      console.error('Error fetching companies:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchControlData = async () => {
    if (!selectedCompany) return;
    
    setLoading(true);
    try {
      const token = localStorage.getItem('hw_token');
      
      const response = await axios.get(`${API}/management-control-table/${selectedCompany}?year=${selectedYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setControlData(response.data);
    } catch (error) {
      console.error('Error fetching control data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBudgetForecasts = async () => {
    if (!selectedCompany) return;
    
    const nextYear = selectedYear + 1;
    setLoading(true);
    
    try {
      const token = localStorage.getItem('hw_token');
      
      const response = await axios.get(`${API}/budget-forecasts/${selectedCompany}/${nextYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setBudgetForecasts(response.data.forecasts || []);
    } catch (error) {
      console.error('Error fetching budget forecasts:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCashFlow = async () => {
    if (!selectedCompany) return;
    
    try {
      const token = localStorage.getItem('hw_token');
      
      const response = await axios.get(`${API}/cash-flow-monthly/${selectedCompany}?year=${selectedYear}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCashFlowData(response.data);
    } catch (error) {
      console.error('Error fetching cash flow:', error);
    }
  };

  const fetchAIInsights = async () => {
    if (!selectedCompany || !selectedYear) {
      alert('Seleziona un\'azienda e un anno');
      return;
    }
    
    setLoadingAI(true);
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.get(
        `${API}/ai/company-insights/${selectedCompany}?year=${selectedYear}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setAiInsights(response.data);
      setShowAIInsights(true);
    } catch (error) {
      console.error('Error fetching AI insights:', error);
      alert('Errore nel caricamento degli insights AI. Riprova.');
    } finally {
      setLoadingAI(false);
    }
  };

  const handleForecastChange = (budgetLineId, newValue) => {
    setEditingForecast({
      ...editingForecast,
      [budgetLineId]: newValue
    });
  };

  const saveForecast = async (budgetLineId) => {
    const newAmount = parseFloat(editingForecast[budgetLineId]);
    
    if (isNaN(newAmount)) {
      alert('Inserisci un valore numerico valido');
      return;
    }
    
    try {
      const token = localStorage.getItem('hw_token');
      const nextYear = selectedYear + 1;
      
      await axios.post(`${API}/budget-forecasts/${selectedCompany}`, {
        budget_line_id: budgetLineId,
        year: nextYear,
        forecast_amount: newAmount
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      // Refresh forecasts
      fetchBudgetForecasts();
      
      // Clear editing state
      const newEditing = { ...editingForecast };
      delete newEditing[budgetLineId];
      setEditingForecast(newEditing);
      
    } catch (error) {
      console.error('Error saving forecast:', error);
      const errorMsg = error.response?.data?.detail || 'Errore durante il salvataggio';
      alert(errorMsg);
    }
  };

  const handleInitializeBudgetLines = async () => {
    if (!selectedCompany) return;
    
    if (!window.confirm('Vuoi inizializzare le voci di bilancio standard per questa società?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('hw_token');
      
      const response = await axios.post(`${API}/initialize-budget-lines/${selectedCompany}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert(response.data.message);
      fetchControlData(); // Refresh data
    } catch (error) {
      console.error('Error initializing budget lines:', error);
      const errorMsg = error.response?.data?.detail || 'Errore durante l\'inizializzazione';
      alert(errorMsg);
    }
  };

  const handleCreateCompany = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.post(`${API}/companies`, newCompany, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Società creata con successo!');
      setShowNewCompany(false);
      setNewCompany({ name: '', code: '', description: '' });
      fetchCompanies();
    } catch (error) {
      console.error('Error creating company:', error);
      const errorMsg = error.response?.data?.detail || 'Errore durante la creazione della società';
      alert(errorMsg);
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('hw_token');
      const response = await axios.post(`${API}/categories`, newCategory, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Categoria creata con successo!');
      setShowNewCategory(false);
      setNewCategory({ name: '', description: '' });
      fetchControlData(); // Refresh to show new category in table
    } catch (error) {
      console.error('Error creating category:', error);
      const errorMsg = error.response?.data?.detail || 'Errore durante la creazione della categoria';
      alert(errorMsg);
    }
  };

  const handleCreateBudgetLine = async (e) => {
    e.preventDefault();
    
    if (!selectedCompany) {
      alert('Seleziona prima una società');
      return;
    }
    
    try {
      const token = localStorage.getItem('hw_token');
      
      const budgetLineData = {
        ...newBudgetLine,
        company_id: selectedCompany,
        forecast: newBudgetLine.forecast ? parseFloat(newBudgetLine.forecast) : 0,
        order: newBudgetLine.order ? parseInt(newBudgetLine.order) : undefined // Let backend generate
      };
      
      const response = await axios.post(`${API}/budget-lines`, budgetLineData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Voce aggiunta con successo!');
      setShowNewBudgetLine(false);
      setNewBudgetLine({ name: '', type: 'revenue', forecast: '', order: '' });
      fetchControlData(); // Refresh data
    } catch (error) {
      console.error('Error creating budget line:', error);
      const errorMsg = error.response?.data?.detail || 'Errore durante la creazione della voce';
      alert(errorMsg);
    }
  };

  const handleUpdateBudgetLine = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('hw_token');
      
      const budgetLineData = {
        name: editBudgetLine.name,
        type: editBudgetLine.type,
        forecast: editBudgetLine.forecast ? parseFloat(editBudgetLine.forecast) : 0,
        order: editBudgetLine.order ? parseInt(editBudgetLine.order) : 0
      };
      
      await axios.put(`${API}/budget-lines/${editBudgetLine.id}`, budgetLineData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Voce modificata con successo!');
      setShowEditBudgetLine(false);
      setEditBudgetLine({ id: '', name: '', type: 'revenue', forecast: '', order: '' });
      fetchControlData(); // Refresh data
    } catch (error) {
      console.error('Error updating budget line:', error);
      const errorMsg = error.response?.data?.detail || 'Errore durante la modifica della voce';
      alert(errorMsg);
    }
  };

  const handleReorderBudgetLine = async (budgetLineId, direction) => {
    try {
      const token = localStorage.getItem('hw_token');
      
      await axios.put(`${API}/budget-lines/${budgetLineId}/reorder?direction=${direction}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      fetchControlData(); // Refresh to show new order
    } catch (error) {
      console.error('Error reordering budget line:', error);
      const errorMsg = error.response?.data?.detail || 'Errore durante il riordino';
      alert(errorMsg);
    }
  };

  const handleDeleteBudgetLine = async (budgetLineId, budgetLineName) => {
    if (!window.confirm(`Vuoi eliminare la voce "${budgetLineName}"?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('hw_token');
      
      await axios.delete(`${API}/budget-lines/${budgetLineId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      alert('Voce eliminata con successo!');
      fetchControlData(); // Refresh data
    } catch (error) {
      console.error('Error deleting budget line:', error);
      const errorMsg = error.response?.data?.detail || 'Errore durante l\'eliminazione';
      alert(errorMsg);
    }
  };

  const openEditDialog = (line) => {
    setEditBudgetLine({
      id: line.id,
      name: line.name,
      type: line.type,
      forecast: line.forecast || '',
      order: line.order || ''
    });
    setShowEditBudgetLine(true);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('it-IT', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const getMonthName = (month) => {
    const months = [
      'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
      'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre'
    ];
    return months[month - 1];
  };

  const getFilterTitle = () => {
    let title = `Controllo di Gestione ${selectedYear}`;
    if (selectedCompany) {
      const company = companies.find(c => c.id === selectedCompany);
      if (company) {
        title += ` - ${company.name}`;
      }
    }
    return title;
  };

  const calculateTotals = (lines, type) => {
    if (!controlData || !lines || lines.length === 0) {
      return {
        forecast: 0,
        monthly: Array(12).fill(0),
        total: 0,
        difference: 0
      };
    }

    let forecast = 0;
    let monthly = Array(12).fill(0);
    let total = 0;

    lines.forEach(line => {
      const lineId = line.id;
      const lineTotals = controlData.totals[lineId] || {};
      const lineMonthly = controlData.monthly_data[lineId] || {};

      forecast += lineTotals.forecast || 0;
      total += lineTotals.actual || 0;

      for (let month = 1; month <= 12; month++) {
        monthly[month - 1] += lineMonthly[month] || 0;
      }
    });

    return {
      forecast,
      monthly,
      total,
      difference: total - forecast
    };
  };

  const getCellColor = (actualValue, monthlyForecast, lineType) => {
    if (actualValue === 0 || actualValue === monthlyForecast) {
      return ''; // Neutro
    }

    // Per le entrate: verde se supera previsione, rosso se inferiore
    if (lineType === 'revenue') {
      if (actualValue > monthlyForecast) {
        return 'bg-green-100 text-green-800 font-semibold';
      } else {
        return 'bg-red-100 text-red-800 font-semibold';
      }
    } 
    // Per le uscite: verde se inferiore alla previsione, rosso se supera
    else {
      if (actualValue < monthlyForecast) {
        return 'bg-green-100 text-green-800 font-semibold';
      } else {
        return 'bg-red-100 text-red-800 font-semibold';
      }
    }
  };

  if (loading) {
    return (
      <Layout title="Controllo di Gestione">
        <div className="flex items-center justify-center min-h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-900"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout title="Controllo di Gestione">
      <div className="space-y-6 fade-in">
        {/* Header */}
        <Card className="p-6 border-0 shadow-lg">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h2 className="text-xl font-semibold text-slate-900">{getFilterTitle()}</h2>
              <p className="text-sm text-slate-600">Analisi costi per società e categorie</p>
            </div>
            <div className="flex gap-3">
              <Button 
                variant="default" 
                className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-lg"
                disabled={!selectedCompany || loadingAI}
                onClick={fetchAIInsights}
              >
                {loadingAI ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Analisi AI...
                  </>
                ) : (
                  <>
                    🤖
                    <span className="ml-2">Insights AI</span>
                  </>
                )}
              </Button>
              
              <Dialog open={showNewBudgetLine} onOpenChange={setShowNewBudgetLine}>
                <DialogTrigger asChild>
                  <Button 
                    variant="default" 
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    disabled={!selectedCompany}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Aggiungi Voce
                  </Button>
                </DialogTrigger>
              </Dialog>
              
              <Dialog open={showNewCompany} onOpenChange={setShowNewCompany}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-blue-200 text-blue-700 hover:bg-blue-50">
                    <Building className="h-4 w-4 mr-2" />
                    Nuova Società
                  </Button>
                </DialogTrigger>
              </Dialog>

              <Dialog open={showCompanyLogos} onOpenChange={setShowCompanyLogos}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-purple-200 text-purple-700 hover:bg-purple-50" data-testid="company-logos-btn">
                    <Building className="h-4 w-4 mr-2" />
                    Loghi Aziende
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-lg">
                  <DialogHeader>
                    <DialogTitle>Loghi Aziende</DialogTitle>
                  </DialogHeader>
                  <p className="text-xs text-slate-500 -mt-2">Il logo viene usato nelle mail di cortesia di avvenuto pagamento, in base all'azienda che paga la fattura. PNG/JPG, max 2MB.</p>
                  <div className="space-y-3 max-h-96 overflow-auto">
                    {companies.map(c => (
                      <div key={c.id} className="flex items-center justify-between gap-3 border border-slate-200 rounded-lg p-3" data-testid={`company-logo-row-${c.id}`}>
                        <div className="flex items-center gap-3 min-w-0">
                          {c.has_logo ? (
                            <img src={`${API}/public/companies/${c.id}/logo?v=${logoRefresh}`} alt={c.name}
                                 className="h-10 w-10 object-contain rounded bg-slate-50 border border-slate-100" />
                          ) : (
                            <div className="h-10 w-10 rounded bg-slate-100 flex items-center justify-center text-slate-400 text-[10px]">—</div>
                          )}
                          <div className="min-w-0">
                            <div className="text-sm font-medium truncate">{c.name}</div>
                            <div className="text-[11px] text-slate-400">{c.has_logo ? 'Logo presente' : 'Nessun logo'}</div>
                          </div>
                        </div>
                        <label className="shrink-0">
                          <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
                                 onChange={(e) => handleLogoUpload(c.id, e.target.files?.[0])} />
                          <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-md border border-slate-300 cursor-pointer hover:bg-slate-50">
                            {logoUploading === c.id ? 'Caricamento…' : (c.has_logo ? 'Sostituisci' : 'Carica logo')}
                          </span>
                        </label>
                      </div>
                    ))}
                  </div>
                </DialogContent>
              </Dialog>
              
              <Dialog open={showNewCategory} onOpenChange={setShowNewCategory}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="border-green-200 text-green-700 hover:bg-green-50">
                    <Plus className="h-4 w-4 mr-2" />
                    Nuova Categoria
                  </Button>
                </DialogTrigger>
              </Dialog>
            </div>
          </div>
        </Card>

        {/* Filters */}
        <Card className="p-4 border-0 shadow-lg">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Società</Label>
              <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona società" />
                </SelectTrigger>
                <SelectContent>
                  {companies.map(company => (
                    <SelectItem key={company.id} value={company.id}>
                      {company.name} ({company.code})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Anno</Label>
              <Select value={selectedYear.toString()} onValueChange={(value) => setSelectedYear(parseInt(value))}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026].map(year => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-end">
              <Button onClick={fetchControlData} className="w-full">
                <Filter className="h-4 w-4 mr-2" />
                Aggiorna
              </Button>
            </div>

            <div className="flex items-end">
              <Button 
                onClick={handleInitializeBudgetLines} 
                variant="outline" 
                className="w-full"
                disabled={!selectedCompany}
              >
                Inizializza Voci
              </Button>
            </div>
          </div>
        </Card>

        {/* Management Control Table */}
        {controlData && (
          <Card className="p-6 border-0 shadow-lg overflow-x-auto">
            <div className="min-w-full">
              <table className="w-full">
                <thead>
                  <tr className="border-b">
                    <th className="text-center p-2 font-semibold min-w-24">Azioni</th>
                    <th className="text-left p-2 font-semibold min-w-48">Voce</th>
                    <th className="text-center p-2 font-semibold min-w-24 bg-yellow-50">Previsione</th>
                    {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                      <th key={month} className="text-center p-2 font-semibold min-w-20 text-xs">
                        {getMonthName(month).slice(0, 3)}
                      </th>
                    ))}
                    <th className="text-center p-2 font-semibold min-w-24 bg-blue-50">Totale</th>
                    <th className="text-center p-2 font-semibold min-w-24 bg-green-50">Diff.</th>
                  </tr>
                </thead>
                <tbody>
                  {/* Revenue Section */}
                  <tr className="bg-green-25 border-b-2">
                    <td colSpan="17" className="p-2 font-semibold text-green-800 bg-green-100">
                      ENTRATE
                    </td>
                  </tr>
                  
                  {controlData.budget_lines.filter(line => line.type === 'revenue').map((line, index, array) => {
                    const monthlyData = controlData.monthly_data[line.id] || {};
                    const totals = controlData.totals[line.id] || {};
                    const monthlyForecast = (totals.forecast || 0) / 12;
                    
                    return (
                      <React.Fragment key={line.id}>
                        <tr className="border-b hover:bg-slate-50">
                          <td className="p-2 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleReorderBudgetLine(line.id, 'up')}
                                disabled={index === 0}
                                className="h-6 w-6 p-0"
                              >
                                <ArrowUp className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleReorderBudgetLine(line.id, 'down')}
                                disabled={index === array.length - 1}
                                className="h-6 w-6 p-0"
                              >
                                <ArrowDown className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(line)}
                                className="h-6 w-6 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteBudgetLine(line.id, line.name)}
                                className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </td>
                          <td className="p-2 font-medium">{line.name}</td>
                          <td className="p-2 text-center bg-yellow-50">
                            <div>{formatCurrency(totals.forecast || 0)}</div>
                            <div className="text-xs text-slate-500">Mens: {formatCurrency(monthlyForecast)}</div>
                          </td>
                          {Array.from({ length: 12 }, (_, i) => i + 1).map(month => {
                            const actualValue = monthlyData[month] || 0;
                            const cellColor = getCellColor(actualValue, monthlyForecast, 'revenue');
                            
                            return (
                              <td key={month} className={`p-2 text-center text-sm ${cellColor}`}>
                                {actualValue > 0 ? formatCurrency(actualValue) : '-'}
                              </td>
                            );
                          })}
                          <td className="p-2 text-center bg-blue-50 font-semibold">
                            {formatCurrency(totals.actual || 0)}
                          </td>
                          <td className={`p-2 text-center bg-green-50 font-semibold ${
                            (totals.difference || 0) >= 0 ? 'text-green-700' : 'text-red-700'
                          }`}>
                            {totals.difference >= 0 ? '+' : ''}{formatCurrency(totals.difference || 0)}
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}

                  {/* TOTALE ENTRATE */}
                  {(() => {
                    const revenueLines = controlData.budget_lines.filter(line => line.type === 'revenue');
                    const revenueTotals = calculateTotals(revenueLines, 'revenue');
                    return (
                      <tr className="border-b-2 bg-green-50 font-bold">
                        <td className="p-2 text-center" colSpan="2">TOTALE ENTRATE</td>
                        <td className="p-2 text-center bg-yellow-100">
                          {formatCurrency(revenueTotals.forecast)}
                        </td>
                        {revenueTotals.monthly.map((amount, index) => (
                          <td key={index} className="p-2 text-center text-sm">
                            {amount > 0 ? formatCurrency(amount) : '-'}
                          </td>
                        ))}
                        <td className="p-2 text-center bg-blue-100">
                          {formatCurrency(revenueTotals.total)}
                        </td>
                        <td className={`p-2 text-center bg-green-100 ${
                          revenueTotals.difference >= 0 ? 'text-green-700' : 'text-red-700'
                        }`}>
                          {revenueTotals.difference >= 0 ? '+' : ''}{formatCurrency(revenueTotals.difference)}
                        </td>
                      </tr>
                    );
                  })()}
                  
                  {/* Expense Section */}
                  <tr className="bg-red-25 border-b-2">
                    <td colSpan="17" className="p-2 font-semibold text-red-800 bg-red-100">
                      USCITE
                    </td>
                  </tr>
                  
                  {controlData.budget_lines.filter(line => line.type === 'expense').map((line, index, array) => {
                    const monthlyData = controlData.monthly_data[line.id] || {};
                    const totals = controlData.totals[line.id] || {};
                    const monthlyForecast = (totals.forecast || 0) / 12;
                    
                    return (
                      <React.Fragment key={line.id}>
                        <tr className="border-b hover:bg-slate-50">
                          <td className="p-2 text-center">
                            <div className="flex items-center justify-center gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleReorderBudgetLine(line.id, 'up')}
                                disabled={index === 0}
                                className="h-6 w-6 p-0"
                              >
                                <ArrowUp className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleReorderBudgetLine(line.id, 'down')}
                                disabled={index === array.length - 1}
                                className="h-6 w-6 p-0"
                              >
                                <ArrowDown className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => openEditDialog(line)}
                                className="h-6 w-6 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                              >
                                <Pencil className="h-3 w-3" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeleteBudgetLine(line.id, line.name)}
                                className="h-6 w-6 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                              >
                                <Trash2 className="h-3 w-3" />
                              </Button>
                            </div>
                          </td>
                          <td className="p-2 font-medium">{line.name}</td>
                          <td className="p-2 text-center bg-yellow-50">
                            <div>{formatCurrency(totals.forecast || 0)}</div>
                            <div className="text-xs text-slate-500">Mens: {formatCurrency(monthlyForecast)}</div>
                          </td>
                          {Array.from({ length: 12 }, (_, i) => i + 1).map(month => {
                            const actualValue = monthlyData[month] || 0;
                            const cellColor = getCellColor(actualValue, monthlyForecast, 'expense');
                            
                            return (
                              <td key={month} className={`p-2 text-center text-sm ${cellColor}`}>
                                {actualValue > 0 ? formatCurrency(actualValue) : '-'}
                              </td>
                            );
                          })}
                          <td className="p-2 text-center bg-blue-50 font-semibold">
                            {formatCurrency(totals.actual || 0)}
                          </td>
                          <td className={`p-2 text-center bg-green-50 font-semibold ${
                            (totals.difference || 0) <= 0 ? 'text-green-700' : 'text-red-700'
                          }`}>
                            {Math.abs(totals.difference || 0) < (totals.forecast || 0) ? '+' : ''}{formatCurrency(Math.abs(totals.difference || 0))}
                          </td>
                        </tr>
                      </React.Fragment>
                    );
                  })}

                  {/* TOTALE USCITE */}
                  {(() => {
                    const expenseLines = controlData.budget_lines.filter(line => line.type === 'expense');
                    const expenseTotals = calculateTotals(expenseLines, 'expense');
                    return (
                      <tr className="border-b-2 bg-red-50 font-bold">
                        <td className="p-2 text-center" colSpan="2">TOTALE USCITE</td>
                        <td className="p-2 text-center bg-yellow-100">
                          {formatCurrency(expenseTotals.forecast)}
                        </td>
                        {expenseTotals.monthly.map((amount, index) => (
                          <td key={index} className="p-2 text-center text-sm">
                            {amount > 0 ? formatCurrency(amount) : '-'}
                          </td>
                        ))}
                        <td className="p-2 text-center bg-blue-100">
                          {formatCurrency(expenseTotals.total)}
                        </td>
                        <td className={`p-2 text-center bg-green-100 ${
                          expenseTotals.difference <= 0 ? 'text-green-700' : 'text-red-700'
                        }`}>
                          {expenseTotals.difference <= 0 ? '+' : ''}{formatCurrency(Math.abs(expenseTotals.difference))}
                        </td>
                      </tr>
                    );
                  })()}
                </tbody>
              </table>
            </div>
          </Card>
        )}

        {/* Statistics Cards */}
        {controlData && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Card className="p-4 border-0 shadow-lg">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <Building className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Voci di Bilancio</p>
                  <p className="text-2xl font-bold text-blue-900">{controlData.budget_lines.length}</p>
                </div>
              </div>
            </Card>

            <Card className="p-4 border-0 shadow-lg">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-green-100 rounded-lg">
                  <TrendingUp className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Entrate</p>
                  <p className="text-2xl font-bold text-green-900">
                    {controlData.budget_lines.filter(l => l.type === 'revenue').length}
                  </p>
                </div>
              </div>
            </Card>

            <Card className="p-4 border-0 shadow-lg">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <BarChart3 className="h-6 w-6 text-red-600" />
                </div>
                <div>
                  <p className="text-sm text-slate-600">Uscite</p>
                  <p className="text-2xl font-bold text-red-900">
                    {controlData.budget_lines.filter(l => l.type === 'expense').length}
                  </p>
                </div>
              </div>
            </Card>
          </div>
        )}

        {/* New Company Dialog */}
        <Dialog open={showNewCompany} onOpenChange={setShowNewCompany}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nuova Società</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateCompany} className="space-y-4">
              <div>
                <Label>Nome Società *</Label>
                <Input
                  value={newCompany.name}
                  onChange={(e) => setNewCompany({...newCompany, name: e.target.value})}
                  required
                  placeholder="Es: HolidayWorld SRL"
                />
              </div>
              <div>
                <Label>Codice Società *</Label>
                <Input
                  value={newCompany.code}
                  onChange={(e) => setNewCompany({...newCompany, code: e.target.value.toUpperCase()})}
                  required
                  placeholder="Es: HW01"
                  maxLength="10"
                  className="uppercase"
                />
              </div>
              <div>
                <Label>Descrizione</Label>
                <Input
                  value={newCompany.description}
                  onChange={(e) => setNewCompany({...newCompany, description: e.target.value})}
                  placeholder="Descrizione opzionale"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowNewCompany(false)} className="flex-1">
                  Annulla
                </Button>
                <Button type="submit" className="flex-1">
                  Crea Società
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* New Category Dialog */}
        <Dialog open={showNewCategory} onOpenChange={setShowNewCategory}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Nuova Categoria</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateCategory} className="space-y-4">
              <div>
                <Label>Nome Categoria *</Label>
                <Input
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                  required
                  placeholder="Es: Pulizie, Manutenzione, Marketing"
                />
              </div>
              <div>
                <Label>Descrizione</Label>
                <Input
                  value={newCategory.description}
                  onChange={(e) => setNewCategory({...newCategory, description: e.target.value})}
                  placeholder="Descrizione opzionale"
                />
              </div>
              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => setShowNewCategory(false)} className="flex-1">
                  Annulla
                </Button>
                <Button type="submit" className="flex-1">
                  Crea Categoria
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* New Budget Line Dialog */}
        <Dialog open={showNewBudgetLine} onOpenChange={setShowNewBudgetLine}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Aggiungi Nuova Voce</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateBudgetLine} className="space-y-4">
              <div>
                <Label>Nome Voce *</Label>
                <Input
                  value={newBudgetLine.name}
                  onChange={(e) => setNewBudgetLine({...newBudgetLine, name: e.target.value})}
                  required
                  placeholder="Es: Prenotazioni, Piattaforme, Pulizie"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Per le prenotazioni: il nome deve contenere "prenotazioni"
                </p>
              </div>
              
              <div>
                <Label>Tipo *</Label>
                <Select 
                  value={newBudgetLine.type} 
                  onValueChange={(value) => setNewBudgetLine({...newBudgetLine, type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="revenue">Entrata</SelectItem>
                    <SelectItem value="expense">Uscita</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Previsione Annuale (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newBudgetLine.forecast}
                  onChange={(e) => setNewBudgetLine({...newBudgetLine, forecast: e.target.value})}
                  placeholder="Inserisci importo"
                />
              </div>

              <div>
                <Label>Ordine di Visualizzazione (opzionale)</Label>
                <Input
                  type="number"
                  value={newBudgetLine.order}
                  onChange={(e) => setNewBudgetLine({...newBudgetLine, order: e.target.value})}
                  placeholder="Lascia vuoto per auto-generazione"
                />
                <p className="text-xs text-slate-500 mt-1">
                  Se non specificato, verrà generato automaticamente. Puoi riordinare dopo con le frecce.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowNewBudgetLine(false);
                    setNewBudgetLine({ name: '', type: 'revenue', forecast: '', order: '' });
                  }} 
                  className="flex-1"
                >
                  Annulla
                </Button>
                <Button type="submit" className="flex-1">
                  Aggiungi Voce
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit Budget Line Dialog */}
        <Dialog open={showEditBudgetLine} onOpenChange={setShowEditBudgetLine}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Modifica Voce</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleUpdateBudgetLine} className="space-y-4">
              <div>
                <Label>Nome Voce *</Label>
                <Input
                  value={editBudgetLine.name}
                  onChange={(e) => setEditBudgetLine({...editBudgetLine, name: e.target.value})}
                  required
                  placeholder="Es: Prenotazioni, Piattaforme, Pulizie"
                />
              </div>
              
              <div>
                <Label>Tipo *</Label>
                <Select 
                  value={editBudgetLine.type} 
                  onValueChange={(value) => setEditBudgetLine({...editBudgetLine, type: value})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="revenue">Entrata</SelectItem>
                    <SelectItem value="expense">Uscita</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Previsione Annuale (€)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={editBudgetLine.forecast}
                  onChange={(e) => setEditBudgetLine({...editBudgetLine, forecast: e.target.value})}
                  placeholder="Inserisci importo"
                />
              </div>

              <div>
                <Label>Ordine di Visualizzazione</Label>
                <Input
                  type="number"
                  value={editBudgetLine.order}
                  onChange={(e) => setEditBudgetLine({...editBudgetLine, order: e.target.value})}
                  placeholder="Numero ordine"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button 
                  type="button" 
                  variant="outline" 
                  onClick={() => {
                    setShowEditBudgetLine(false);
                    setEditBudgetLine({ id: '', name: '', type: 'revenue', forecast: '', order: '' });
                  }} 
                  className="flex-1"
                >
                  Annulla
                </Button>
                <Button type="submit" className="flex-1">
                  Salva Modifiche
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* AI Insights Dialog */}
        <Dialog open={showAIInsights} onOpenChange={setShowAIInsights}>
          <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="text-2xl font-bold flex items-center gap-2">
                🤖 AI Business Insights - {aiInsights?.company_name || 'Analisi Aziendale'}
              </DialogTitle>
              <p className="text-sm text-gray-600">
                Analisi multi-esperto generata da intelligenza artificiale per l'anno {selectedYear}
              </p>
            </DialogHeader>
            
            {aiInsights && (
              <div className="space-y-6 mt-4">
                {/* Financial Summary */}
                <Card className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                  <h3 className="text-lg font-bold text-blue-900 mb-3">📊 Riepilogo Finanziario</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-blue-700">Ricavi Totali</p>
                      <p className="text-xl font-bold text-blue-900">€{aiInsights.financial_summary.total_revenue.toLocaleString('it-IT', {minimumFractionDigits: 2})}</p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-700">Costi Totali</p>
                      <p className="text-xl font-bold text-red-700">€{aiInsights.financial_summary.total_costs.toLocaleString('it-IT', {minimumFractionDigits: 2})}</p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-700">Margine Netto</p>
                      <p className="text-xl font-bold text-green-700">€{aiInsights.financial_summary.total_margin.toLocaleString('it-IT', {minimumFractionDigits: 2})}</p>
                    </div>
                    <div>
                      <p className="text-xs text-blue-700">Margine %</p>
                      <p className="text-xl font-bold text-purple-700">{aiInsights.financial_summary.margin_percentage.toFixed(1)}%</p>
                    </div>
                  </div>
                </Card>

                {/* Sintesi Generale */}
                <Card className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 border-purple-200">
                  <h3 className="text-lg font-bold text-purple-900 mb-2">💡 Sintesi Generale</h3>
                  <p className="text-gray-700">{aiInsights.ai_insights.sintesi_generale}</p>
                </Card>

                {/* Priorità Assoluta */}
                <Card className="p-4 bg-gradient-to-r from-red-50 to-orange-50 border-red-300">
                  <h3 className="text-lg font-bold text-red-900 mb-3 flex items-center gap-2">
                    🚨 Priorità Assoluta - Top 3 Azioni Immediate
                  </h3>
                  <ul className="space-y-2">
                    {aiInsights.ai_insights.priorita_assoluta.map((action, idx) => (
                      <li key={idx} className="flex items-start gap-2">
                        <span className="font-bold text-red-600">{idx + 1}.</span>
                        <span className="text-gray-700">{action}</span>
                      </li>
                    ))}
                  </ul>
                </Card>

                {/* CFO Insights */}
                <Card className="p-4 bg-gradient-to-r from-green-50 to-emerald-50 border-green-200">
                  <h3 className="text-lg font-bold text-green-900 mb-3 flex items-center gap-2">
                    💰 CFO / Financial Strategist
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-green-800 mb-2">Ottimizzazione Costi</h4>
                      <ul className="space-y-1 ml-4">
                        {aiInsights.ai_insights.cfo_insights.ottimizzazione_costi.map((item, idx) => (
                          <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                            <span className="text-green-600">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-green-800 mb-2">Aumento Marginalità</h4>
                      <ul className="space-y-1 ml-4">
                        {aiInsights.ai_insights.cfo_insights.aumento_marginalita.map((item, idx) => (
                          <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                            <span className="text-green-600">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-green-800 mb-2">Pricing Strategy</h4>
                      <ul className="space-y-1 ml-4">
                        {aiInsights.ai_insights.cfo_insights.pricing_strategy.map((item, idx) => (
                          <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                            <span className="text-green-600">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Card>

                {/* Marketing Insights */}
                <Card className="p-4 bg-gradient-to-r from-orange-50 to-yellow-50 border-orange-200">
                  <h3 className="text-lg font-bold text-orange-900 mb-3 flex items-center gap-2">
                    📢 Marketing Strategist
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-orange-800 mb-2">Piattaforme</h4>
                      <ul className="space-y-1 ml-4">
                        {aiInsights.ai_insights.marketing_insights.piattaforme.map((item, idx) => (
                          <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                            <span className="text-orange-600">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-orange-800 mb-2">Pricing & Occupancy</h4>
                      <ul className="space-y-1 ml-4">
                        {aiInsights.ai_insights.marketing_insights.pricing_occupancy.map((item, idx) => (
                          <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                            <span className="text-orange-600">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-orange-800 mb-2">Strategie Acquisizione</h4>
                      <ul className="space-y-1 ml-4">
                        {aiInsights.ai_insights.marketing_insights.strategie_acquisizione.map((item, idx) => (
                          <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                            <span className="text-orange-600">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Card>

                {/* Data Analyst Insights */}
                <Card className="p-4 bg-gradient-to-r from-cyan-50 to-blue-50 border-cyan-200">
                  <h3 className="text-lg font-bold text-cyan-900 mb-3 flex items-center gap-2">
                    📈 Data Analyst
                  </h3>
                  
                  <div className="space-y-4">
                    <div>
                      <h4 className="font-semibold text-cyan-800 mb-2">Pattern Identificati</h4>
                      <ul className="space-y-1 ml-4">
                        {aiInsights.ai_insights.data_insights.pattern_identificati.map((item, idx) => (
                          <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                            <span className="text-cyan-600">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-cyan-800 mb-2">KPI Critici da Monitorare</h4>
                      <ul className="space-y-1 ml-4">
                        {aiInsights.ai_insights.data_insights.kpi_critici.map((item, idx) => (
                          <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                            <span className="text-cyan-600">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-semibold text-cyan-800 mb-2">Esperimenti Proposti</h4>
                      <ul className="space-y-1 ml-4">
                        {aiInsights.ai_insights.data_insights.esperimenti_proposti.map((item, idx) => (
                          <li key={idx} className="text-sm text-gray-700 flex items-start gap-2">
                            <span className="text-cyan-600">•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Card>

                {/* Quick Wins */}
                <Card className="p-4 bg-gradient-to-r from-yellow-50 to-amber-50 border-yellow-300">
                  <h3 className="text-lg font-bold text-yellow-900 mb-3 flex items-center gap-2">
                    ⚡ Quick Wins - Azioni Rapide ad Alto Impatto
                  </h3>
                  <div className="space-y-3">
                    {aiInsights.ai_insights.quick_wins.map((win, idx) => (
                      <div key={idx} className="bg-white p-3 rounded-lg border border-yellow-200">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <p className="font-semibold text-gray-900">{win.azione}</p>
                          <Badge className={`
                            ${win.effort === 'basso' ? 'bg-green-100 text-green-800' : ''}
                            ${win.effort === 'medio' ? 'bg-yellow-100 text-yellow-800' : ''}
                            ${win.effort === 'alto' ? 'bg-red-100 text-red-800' : ''}
                          `}>
                            {win.effort}
                          </Badge>
                        </div>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-green-700 font-medium">💰 {win.impatto}</span>
                          <span className="text-blue-700">🕐 {win.timeframe}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* Long Term Projects */}
                <Card className="p-4 bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
                  <h3 className="text-lg font-bold text-indigo-900 mb-3 flex items-center gap-2">
                    🎯 Progetti Lungo Termine - Investimenti Strategici
                  </h3>
                  <div className="space-y-3">
                    {aiInsights.ai_insights.progetti_lungo_termine.map((project, idx) => (
                      <div key={idx} className="bg-white p-3 rounded-lg border border-indigo-200">
                        <p className="font-semibold text-gray-900 mb-2">{project.progetto}</p>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-green-700 font-medium">💰 {project.impatto}</span>
                          <span className="text-purple-700">📅 {project.timeframe}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </Card>

                {/* AI Info */}
                <Card className="p-4 bg-gradient-to-r from-purple-100 to-pink-100 border-purple-300">
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">🤖</span>
                    <div>
                      <h4 className="font-bold text-purple-900 mb-1">Come funziona l'AI</h4>
                      <p className="text-sm text-purple-800">
                        Questi insights sono generati da un team virtuale di 3 esperti AI (CFO, Marketing Strategist, Data Analyst) 
                        che analizzano i tuoi dati reali usando GPT-4o. I consigli sono specifici per la tua azienda e basati 
                        su metriche concrete di performance, costi, piattaforme e trend mensili.
                      </p>
                    </div>
                  </div>
                </Card>

                {/* Close Button */}
                <div className="flex justify-end">
                  <Button 
                    onClick={() => setShowAIInsights(false)}
                    className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                  >
                    Chiudi
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ManagementControl;